/* === IMPORTS === */
import { createReducer } from "@reduxjs/toolkit"

import { makeActions } from "reduxModules/utiliducks"
import { makeLib } from "utils/misc"
import {
  getUserNotificationsRequest,
  updateUserNotificationRequest,
  updateUserNotificationsRequest,
} from "utils/notification/user-notification"
import {
  addMessage,
  GLOBAL_NOTIFICATIONS,
  MESSAGE_TYPE_ERROR,
} from "utils/notifications"

/* === ACTIONS === */
const { setUserNotificationsAction, updateUserNotificationAction, setPagingAction } =
  makeActions("userNotifications", [
    "setUserNotificationsAction",
    "updateUserNotificationAction",
    "setPagingAction",
  ])

/* === INITIAL STATE === */
const initialState = {
  notifications: [],
  notificationsLib: {},
  paging: { previous_cursor: "", next_cursor: "" },
}

/* === Reducer === */
export default createReducer(initialState, {
  [setUserNotificationsAction]: (state, { payload: { notifications = [] } }) => ({
    ...state,
    notifications,
    notificationsLib: makeLib({ data: notifications }),
  }),
  [updateUserNotificationAction]: (state, { payload: { id, notification } }) => ({
    ...state,
    notifications: state.notifications.map(notif =>
      notif.id === id ? notification : notif
    ),
    notificationsLib: {
      ...state.notificationsLib,
      [id]: { ...state.notificationsLib[id], ...notification },
    },
  }),
  [setPagingAction]: (state, { payload: { paging } }) => ({
    ...state,
    paging,
  }),
})

/* === ERROR HANDLING === */
const handleNotificationError = (error, action) => {
  const isInsufficientCredentials = error.message.includes(
    "Access credentials are not sufficient to access this resource"
  )
  
  console.error(`${error.name}: ${error.message}`)
  
  if (!isInsufficientCredentials) {
    addMessage({
      target: GLOBAL_NOTIFICATIONS,
      text: `User notification ${action}`,
      subtext: error.message,
      type: MESSAGE_TYPE_ERROR,
    })
  }
}

/* === DISPATCHERS === */
const getUserFromState = (getState) => {
  return getState()?.authentication?.userInfo?.username
}

export const getUserNotifications = () => async (dispatch, getState) => {
  try {
    const user = getUserFromState(getState)
    if (!user) return

    const { data, paging } = await getUserNotificationsRequest(user, "?status=received")
    const notifications = data || []

    dispatch(setUserNotificationsAction({ notifications }))
    if (paging) dispatch(setPaging({ paging }))
  } catch (error) {
    handleNotificationError(error, "could not be retrieved")
  }
}

export const getUserNextNotifications = () => async (dispatch, getState) => {
  try {
    const user = getUserFromState(getState)
    if (!user) return

    const { userNotifications } = getState()
    const { data, paging } = await getUserNotificationsRequest(user, {
      next_cursor: userNotifications.paging.next_cursor,
    })

    dispatch(setPaging({ paging }))
    dispatch(setUserNotificationsAction({
      notifications: [...userNotifications.notifications, ...data],
    }))
  } catch (error) {
    handleNotificationError(error, "could not be retrieved")
  }
}

export const dismissAllUserNotifications = () => async (dispatch, getState) => {
  try {
    const user = getUserFromState(getState)
    if (!user) return

    const { userNotifications } = getState()
    const notificationsToUpdate = userNotifications.notifications
      .filter(Boolean)
      .map(({ id }) => ({ id, status: "read" }))

    if (notificationsToUpdate.length === 0) return

    await updateUserNotificationsRequest(user, notificationsToUpdate)
    await dispatch(getUserNotifications())
  } catch (error) {
    handleNotificationError(error, "could not be updated")
  }
}

export const updateUserNotificationStatusToRead = (id) => async (dispatch, getState) => {
  try {
    const user = getUserFromState(getState)
    if (!user) return

    const response = await updateUserNotificationRequest(user, id, { status: "read" })
    dispatch(updateUserNotificationAction({ id, notification: response }))
  } catch (error) {
    handleNotificationError(error, "could not be updated")
  }
}

/* === UTILS === */
export const setPaging = ({ paging }) => setPagingAction({ paging })
